import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Truck, User, ChevronsUpDown, Plus, Trash2, Check, Save } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const F = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>
);

/** e-İrsaliye şoför + araç seçimi — kayıtlılardan seç / yeni ekle / sil. form: {carrier_plate, driver_name, driver_surname, driver_tckn} */
export default function DriverVehiclePicker({ form, setForm }) {
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [dOpen, setDOpen] = useState(false);
  const [vOpen, setVOpen] = useState(false);
  const load = () => {
    api.get("/irsaliyeler/drivers").then((r) => setDrivers(r.data || [])).catch(() => {});
    api.get("/irsaliyeler/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {});
  };
  useEffect(load, []);

  const pickDriver = (d) => { setForm((f) => ({ ...f, driver_name: d.name, driver_surname: d.surname || "", driver_tckn: d.tckn || "" })); setDOpen(false); };
  const pickVehicle = (v) => { setForm((f) => ({ ...f, carrier_plate: v.plate })); setVOpen(false); };
  const saveDriver = async () => {
    if (!form.driver_name?.trim()) return toast.error("Şoför adı girin");
    try { await api.post("/irsaliyeler/drivers", { name: form.driver_name, surname: form.driver_surname, tckn: form.driver_tckn }); toast.success("Şoför kaydedildi"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveVehicle = async () => {
    if (!form.carrier_plate?.trim()) return toast.error("Plaka girin");
    try { await api.post("/irsaliyeler/vehicles", { plate: form.carrier_plate }); toast.success("Araç kaydedildi"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delDriver = async (e, d) => { e.stopPropagation(); await api.delete(`/irsaliyeler/drivers/${d.id}`); load(); };
  const delVehicle = async (e, v) => { e.stopPropagation(); await api.delete(`/irsaliyeler/vehicles/${v.id}`); load(); };
  const curDriver = drivers.find((d) => d.name === form.driver_name && (d.surname || "") === (form.driver_surname || ""));
  const curVehicle = vehicles.find((v) => v.plate === (form.carrier_plate || "").toUpperCase());

  return (
    <div className="border rounded-lg p-3 space-y-3 mt-1" data-testid="irs-transport">
      <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Truck className="h-3.5 w-3.5" />Taşıma / Araç Bilgileri</Label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Şoför */}
        <div className="rounded-lg bg-muted/40 p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1"><User className="h-3 w-3" />Şoför</span>
            <Popover open={dOpen} onOpenChange={setDOpen}>
              <PopoverTrigger asChild>
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs" data-testid="irs-driver-pick"><ChevronsUpDown className="h-3 w-3 mr-1" />Kayıtlı Şoförler ({drivers.length})</Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-1" align="end">
                {drivers.length === 0 ? <div className="px-3 py-4 text-xs text-muted-foreground text-center">Kayıtlı şoför yok — bilgileri girip "Kaydet"e basın.</div> : drivers.map((d) => (
                  <button key={d.id} type="button" onClick={() => pickDriver(d)} className={cn("w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted", curDriver?.id === d.id && "bg-primary/5")} data-testid={`irs-driver-opt-${d.id}`}>
                    <Check className={cn("h-3.5 w-3.5 shrink-0", curDriver?.id === d.id ? "opacity-100 text-primary" : "opacity-0")} />
                    <span className="flex-1 min-w-0"><span className="block truncate font-medium">{d.name} {d.surname}</span>{d.tckn && <span className="block text-[10px] text-muted-foreground font-mono">{d.tckn}</span>}</span>
                    <span role="button" onClick={(e) => delDriver(e, d)} className="text-muted-foreground hover:text-destructive p-1" title="Kayıtlı şoförü sil" data-testid={`irs-driver-del-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></span>
                  </button>
                ))}
              </PopoverContent>
            </Popover>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <F label="Adı"><Input value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} data-testid="irs-driver-name" /></F>
            <F label="Soyadı"><Input value={form.driver_surname} onChange={(e) => setForm({ ...form, driver_surname: e.target.value })} data-testid="irs-driver-surname" /></F>
            <F label="TCKN"><Input value={form.driver_tckn} onChange={(e) => setForm({ ...form, driver_tckn: e.target.value.replace(/\D/g, "").slice(0, 11) })} placeholder="11 hane" data-testid="irs-driver-tckn" /></F>
            <div className="flex items-end">
              {curDriver ? <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1 h-10"><Check className="h-3.5 w-3.5" />Kayıtlı şoför</span>
                : <Button type="button" size="sm" variant="secondary" className="h-10 w-full" onClick={saveDriver} disabled={!form.driver_name?.trim()} data-testid="irs-driver-save"><Save className="h-3.5 w-3.5 mr-1" />Şoförü Kaydet</Button>}
            </div>
          </div>
        </div>
        {/* Araç */}
        <div className="rounded-lg bg-muted/40 p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1"><Truck className="h-3 w-3" />Araç</span>
            <Popover open={vOpen} onOpenChange={setVOpen}>
              <PopoverTrigger asChild>
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs" data-testid="irs-vehicle-pick"><ChevronsUpDown className="h-3 w-3 mr-1" />Kayıtlı Araçlar ({vehicles.length})</Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-1" align="end">
                {vehicles.length === 0 ? <div className="px-3 py-4 text-xs text-muted-foreground text-center">Kayıtlı araç yok — plakayı girip "Kaydet"e basın.</div> : vehicles.map((v) => (
                  <button key={v.id} type="button" onClick={() => pickVehicle(v)} className={cn("w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted", curVehicle?.id === v.id && "bg-primary/5")} data-testid={`irs-vehicle-opt-${v.id}`}>
                    <Check className={cn("h-3.5 w-3.5 shrink-0", curVehicle?.id === v.id ? "opacity-100 text-primary" : "opacity-0")} />
                    <span className="flex-1 font-mono font-bold tracking-wider">{v.plate}</span>
                    <span role="button" onClick={(e) => delVehicle(e, v)} className="text-muted-foreground hover:text-destructive p-1" title="Kayıtlı aracı sil" data-testid={`irs-vehicle-del-${v.id}`}><Trash2 className="h-3.5 w-3.5" /></span>
                  </button>
                ))}
              </PopoverContent>
            </Popover>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <F label="Plaka"><Input value={form.carrier_plate} onChange={(e) => setForm({ ...form, carrier_plate: e.target.value.toUpperCase() })} placeholder="34 ABC 123" className="font-mono tracking-wider" data-testid="irs-plate" /></F>
            <div className="flex items-end">
              {curVehicle ? <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1 h-10"><Check className="h-3.5 w-3.5" />Kayıtlı araç</span>
                : <Button type="button" size="sm" variant="secondary" className="h-10 w-full" onClick={saveVehicle} disabled={!form.carrier_plate?.trim()} data-testid="irs-vehicle-save"><Plus className="h-3.5 w-3.5 mr-1" />Aracı Kaydet</Button>}
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground">İrsaliye kaydedilince kullanılan şoför ve plaka otomatik olarak listeye eklenir.</p>
        </div>
      </div>
    </div>
  );
}
